import React from "react";
import { useNavigate } from "react-router-dom";
import { FiPaperclip } from "react-icons/fi";
import Lozenge from "../../BaseUI/Lozenge";
import { Table, Tr, Td } from "../../BaseUI/Table";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../../../views/pages/shared/ListViewOptionsPanel";
import ApprovalRejectModal from "../ApprovalRejectModal";
import ApprovalRowActions from "../ApprovalRowActions";
import ApprovalDetailModal from "../ApprovalDetailModal";
import ApprovalAttachmentModal from "../ApprovalAttachmentModal";
import ApprovalAttachmentsModal from "../ApprovalAttachmentsModal";
import { useFormatValue } from "../../../hooks/useFormatValue";
import { useApprovalInbox } from "../../../hooks/useApprovalInbox";
import ApprovalResultBadge from "../../../approvals/ApprovalResultBadge";
import ApprovalStepLogsTable from "../../../approvals/ApprovalStepLogsTable";
import ProductPrice from "../ProductPrice";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";
import { translate } from "../../../hooks/translate";
import {
  resolveCompanyNameFromItem,
  parseRateNum,
  formatRateDisplay,
} from "../../../approvals/plugins/order/orderDetailUtils";
import {
  getApprovalPlugin,
  getItemApprovalLabels,
} from "../../../approvals/registry";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { limitWords } from "../../../utils/textDisplay";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import {
  collectApprovalAttachments,
  downloadApprovalAttachment,
  shareApprovalAttachment,
} from "../../../approvals/attachments";
import { useAuth } from "../../../services/useAuth";

const sortText = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), "tr", {
    sensitivity: "base",
    numeric: true,
  });

const approvalCreatorName = (item) =>
  item?.createdBy?.name || item?.requestedBy?.name || "";

const previousStepApproverName = (item) =>
  item?.previousStepApprovedBy?.name || "";

const approvalSortDate = (item) => {
  const raw =
    item?.status === "pending"
      ? item?.requestedAt
      : item?.resolvedAt || item?.requestedAt;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

const APPROVAL_TABLE_HEADERS = [
  {
    id: 0,
    name: "Tür",
    dataType: "text",
    customSorter: (a, b) =>
      sortText(a.typeLabel || a.type, b.typeLabel || b.type),
  },
  {
    id: 1,
    name: "Başlık",
    dataType: "text",
    customSorter: (a, b) => sortText(a.title, b.title),
  },
  {
    id: 2,
    name: "Firma",
    dataType: "text",
    customSorter: (a, b) =>
      sortText(a.summary?.companyName, b.summary?.companyName),
  },
  {
    id: 3,
    name: "Tutar",
    dataType: "number",
    customSorter: (a, b) =>
      (Number(a.summary?.totalAmount) || 0) -
      (Number(b.summary?.totalAmount) || 0),
  },
  {
    id: 9,
    name: translate("UI.EXCHANGE_RATE") || "Döviz Kuru",
    dataType: "number",
  },
  {
    id: 4,
    name: "Durum",
    dataType: "text",
    customSorter: (a, b) => sortText(a.status, b.status),
  },
  {
    id: 5,
    name: "Oluşturan",
    dataType: "text",
    customSorter: (a, b) =>
      sortText(approvalCreatorName(a), approvalCreatorName(b)),
  },
  {
    id: 6,
    name: "Önceki Onaylayan",
    dataType: "text",
    customSorter: (a, b) =>
      sortText(previousStepApproverName(a), previousStepApproverName(b)),
  },
  {
    id: 7,
    name: "Tarih",
    dataType: "date",
    customSorter: (a, b) => approvalSortDate(a) - approvalSortDate(b),
  },
  { id: 8, name: "İşlemler", sortable: false },
];

/** Fallback row for entity types that ship no plugin renderer. */
const defaultRowCells = (item, { format, fx }, history = false, visibleCols) => {
  const show = (id) => !visibleCols || visibleCols.includes(id);
  const companyName = resolveCompanyNameFromItem(item);
  const date = history
    ? item.resolvedAt || item.requestedAt
    : item.requestedAt;
  const rate = item.summary?.exchangeRate ?? item.entity?.exchangeRate;
  const rateNum = parseRateNum(rate);
  const isFallback = !rateNum && !!fx?.rate;
  const displayRate = rateNum ?? parseRateNum(fx?.rate);

  return (
    <>
      {show(0) && <Td>{item.typeLabel || item.type}</Td>}
      {show(1) && (
        <Td>
          <span title={item.title || undefined}>{limitWords(item.title)}</span>
        </Td>
      )}
      {show(2) && (
        <Td>
          <span title={companyName !== "-" ? companyName : undefined}>
            {limitWords(companyName === "-" ? "" : companyName)}
          </span>
        </Td>
      )}
      {show(3) && (
        <Td>
          <span className="fw-semibold text-primary">
            <ProductPrice
              value={item.summary?.totalAmount || 0}
              rate={rateNum}
              showFallbackMark={isFallback}
            />
          </span>
        </Td>
      )}
      {show(9) && (
        <Td>
          {displayRate ? (
            <span>
              {formatRateDisplay(displayRate)}
              {isFallback && (
                <span className="text-warning fw-bold ms-1" title="Güncel kur">*</span>
              )}
            </span>
          ) : (
            "-"
          )}
        </Td>
      )}
      {show(4) && (
        <Td>
          <ApprovalResultBadge item={item} />
        </Td>
      )}
      {show(5) && (
        <Td>
          {history
            ? item.resolvedBy?.name || item.requestedBy?.name || "-"
            : item.createdBy?.name || item.requestedBy?.name || "-"}
        </Td>
      )}
      {show(6) && <Td>{item.previousStepApprovedBy?.name || "-"}</Td>}
      {show(7) && <Td>{date ? formatInstantDateShortTr(date) : "-"}</Td>}
    </>
  );
};

export const ApprovalInboxUI = ({
  inbox,
  canWrite = false,
  canWriteEntity,
  stage = "admin",
  emptyMessage = "Kayıt bulunmuyor.",
  filterSections = [],
  activeFilterCount = 0,
  onApplyFilters,
  onResetFilters,
}) => {
  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [attachmentTarget, setAttachmentTarget] = React.useState(null);
  const [attachmentsItem, setAttachmentsItem] = React.useState(null);
  const {
    items,
    loading,
    listMode,
    readOnly,
    canActOnItem,
    canWriteForItem,
    viewItem,
    setViewItem,
    approve,
    startReject,
    cancelReject,
    confirmReject,
    rejectTarget,
    rejectReason,
    setRejectReason,
    isActionLoading,
    getPlugin,
    getStageConfig,
  } = inbox;

  const fx = useExchangeRateOptional();

  const columns = React.useMemo(
    () =>
      APPROVAL_TABLE_HEADERS.map((col) => {
        if (col.id === 9) {
          return {
            ...col,
            customSorter: (a, b) => {
              const rateA =
                parseRateNum(a.summary?.exchangeRate ?? a.entity?.exchangeRate) ??
                parseRateNum(fx?.rate) ??
                0;
              const rateB =
                parseRateNum(b.summary?.exchangeRate ?? b.entity?.exchangeRate) ??
                parseRateNum(fx?.rate) ??
                0;
              return rateA - rateB;
            },
          };
        }
        return col;
      }),
    [fx?.rate],
  );
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("approval-inbox-list", columns);
  const viewPlugin = viewItem ? getPlugin(viewItem) : null;
  const viewStage = viewItem ? getStageConfig(viewItem) : null;
  const rejectStage = rejectTarget ? getStageConfig(rejectTarget) : null;
  const itemCanWrite = (item) =>
    typeof canWriteEntity === "function"
      ? canWriteEntity(item?.type || item?.entityType)
      : typeof canWriteForItem === "function"
        ? canWriteForItem(item)
        : canWrite;

  const ctx = { format, canWrite, navigate, visibleColumns, fx };

  const handleApproveWithConfirm = (item) => {
    const labels = getItemApprovalLabels(item, stage);
    const actionLabel = labels?.approveActionLabel || "Onayla";
    const modalTitle = labels?.approveModalTitle || actionLabel;
    const modalMessage =
      labels?.approveModalMessage ||
      `${item?.title || "Bu kaydı"} için '${actionLabel}' işlemini gerçekleştirmek istediğinize emin misiniz?`;
    if (item?.allowFileUpload) {
      setAttachmentTarget(item);
      return;
    }
    confirm({
      title: modalTitle,
      message: modalMessage,
      appearance: "primary",
      confirmButtonText: actionLabel,
      cancelButtonText: "İptal",
      onConfirm: () => approve(item),
    });
  };

  const handleRejectWithConfirm = (item, onCloseView) => {
    const labels = getItemApprovalLabels(item, stage);
    const rejectLabel = labels?.rejectActionLabel || "Reddet";
    const rejectTitle = labels?.rejectModalTitle || rejectLabel;
    const rejectMessage =
      labels?.rejectModalMessage ||
      `${item?.title || "Bu kaydı"} reddetmek istediğinize emin misiniz?`;
    confirm({
      title: rejectTitle,
      message: rejectMessage,
      appearance: "danger",
      confirmButtonText: "Devam",
      cancelButtonText: "İptal",
      onConfirm: () => {
        onCloseView?.();
        startReject(item);
      },
    });
  };

  const viewLabels = viewItem ? getItemApprovalLabels(viewItem, stage) : null;
  const rejectLabels = rejectTarget
    ? getItemApprovalLabels(rejectTarget, stage)
    : null;

  const detailTitle =
    viewStage?.detailTitle || viewPlugin?.stages?.admin?.detailTitle || "Detay";

  return (
    <>
      <Table
        key={`${listMode}-${columns.length}`}
        columns={columns}
        visibleColumns={visibleColumns}
        tableId="approval-inbox-list"
        loading={loading}
        search={true}
        viewOptions={
          <ListViewOptionsPanel
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={toggleColumnVisibility}
            onResetColumns={resetToDefault}
            filterSections={filterSections}
            activeFilterCount={activeFilterCount}
            onApply={onApplyFilters}
            onReset={onResetFilters}
          />
        }
      >
        {items.map((item) => {
          const plugin = getPlugin(item);
          // "all" mixes pending and resolved rows, so key off the row itself.
          const asHistory =
            listMode === "history" ||
            (listMode === "all" && item.status !== "pending");
          const renderCells = asHistory
            ? plugin?.inbox?.renderHistoryCells
            : plugin?.inbox?.renderRowCells;
          const rowCells =
            renderCells?.(item, ctx) ||
            defaultRowCells(item, ctx, asHistory, visibleColumns);
          const itemCanAct = itemCanWrite(item) && canActOnItem(item);
          const attachmentCount = collectApprovalAttachments(item).length;

          return (
            <Tr key={item.id} id={item.id} data={item}>
              {rowCells}
              {visibleColumns.includes(8) && (
              <Td>
                <ApprovalRowActions
                  canAct={itemCanAct}
                  readOnly={!itemCanAct}
                  onView={() => setViewItem(item)}
                  onApprove={() => handleApproveWithConfirm(item)}
                  onReject={() => handleRejectWithConfirm(item)}
                  approveLoading={isActionLoading(item, "approve")}
                  approveLabel={
                    getItemApprovalLabels(item, stage)?.approveActionLabel
                  }
                  rejectLabel={
                    getItemApprovalLabels(item, stage)?.rejectActionLabel
                  }
                >
                  {attachmentCount > 0 && (
                    <FiPaperclip
                      className="cursor-pointer text-grey"
                      style={{ fontSize: "20px", cursor: "pointer" }}
                      title={`Eklenen dosyalar (${attachmentCount})`}
                      onClick={() => setAttachmentsItem(item)}
                    />
                  )}
                </ApprovalRowActions>
              </Td>
              )}
            </Tr>
          );
        })}
      </Table>
      {items.some(
        (item) =>
          !parseRateNum(
            item.summary?.exchangeRate ?? item.entity?.exchangeRate,
          ),
      ) && (
        <div className="text-muted small mt-2 d-flex align-items-center gap-1">
          <span className="text-warning fw-bold">*</span>
          <span>
            {translate("UI.FALLBACK_EXCHANGE_RATE_NOTE") ||
              "İşaretli tutarlar ve kurlar, kayda ait kur bulunmadığı için güncel kur ile hesaplanmıştır."}
          </span>
        </div>
      )}
      {!loading && items.length === 0 && (
        <p className="text-center text-muted py-4 mb-0">{emptyMessage}</p>
      )}

      <ApprovalDetailModal
        isOpen={!!viewItem}
        onClose={() => setViewItem(null)}
        title={detailTitle}
        header={
          viewPlugin?.inbox?.renderDetailHeader?.(viewItem, ctx) ?? detailTitle
        }
        width={viewPlugin?.inbox?.detailWidth || "large"}
        scrollBehavior={viewPlugin?.inbox?.detailScrollBehavior}
        canAct={viewItem && itemCanWrite(viewItem) && canActOnItem(viewItem)}
        readOnly={!viewItem || !canActOnItem(viewItem)}
        approveLabel={
          viewLabels?.approveActionLabel || viewStage?.approveLabel || "Onayla"
        }
        rejectLabel={viewStage?.rejectLabel || "Reddet"}
        onApprove={() => handleApproveWithConfirm(viewItem)}
        onReject={() =>
          handleRejectWithConfirm(viewItem, () => setViewItem(null))
        }
        approveLoading={viewItem && isActionLoading(viewItem, "approve")}
      >
        {viewItem ? (
          <div className="order-detail">
            {viewPlugin?.inbox?.renderDetailBody?.(viewItem, {
              ...ctx,
              detailSection: "info",
            }) ?? <p className="text-muted mb-0">Detay tanımlı değil.</p>}
            <ApprovalStepLogsTable logs={viewItem.logs} />
            {viewPlugin?.inbox?.renderDetailBody?.(viewItem, {
              ...ctx,
              detailSection: "models",
            })}
            {viewPlugin?.inbox?.renderDetailBody?.(viewItem, {
              ...ctx,
              detailSection: "descriptions",
            })}
            {viewItem?.status && (
              <p className="mt-3 mb-0">
                <ApprovalResultBadge item={viewItem} />
              </p>
            )}
          </div>
        ) : null}
      </ApprovalDetailModal>

      <ApprovalRejectModal
        isOpen={!!rejectTarget}
        onClose={cancelReject}
        title={
          rejectLabels?.rejectModalTitle ||
          rejectStage?.rejectModalTitle ||
          "Reddet"
        }
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onConfirm={confirmReject}
        loading={rejectTarget && isActionLoading(rejectTarget, "reject")}
        variant="simple"
      />
      <ApprovalAttachmentModal
        isOpen={!!attachmentTarget}
        onClose={() => setAttachmentTarget(null)}
        title={
          attachmentTarget
            ? getItemApprovalLabels(attachmentTarget, stage)
                ?.approveModalTitle || "Onayla"
            : "Onayla"
        }
        loading={
          attachmentTarget &&
          isActionLoading(attachmentTarget, "approve")
        }
        onConfirm={async (attachment) => {
          if (!attachmentTarget) return;
          await approve(attachmentTarget, { attachment });
          setAttachmentTarget(null);
        }}
      />
      <ApprovalAttachmentsModal
        isOpen={!!attachmentsItem}
        onClose={() => setAttachmentsItem(null)}
        attachments={
          attachmentsItem ? collectApprovalAttachments(attachmentsItem) : []
        }
        onShare={(attachment) =>
          shareApprovalAttachment(attachment, getToken())
        }
        onDownload={(attachment) =>
          downloadApprovalAttachment(attachment, getToken())
        }
      />
    </>
  );
};

const ApprovalInboxContainer = ({
  canWrite,
  canWriteEntity,
  stage,
  listMode,
  queueType,
  queueParams,
  onNotify,
  emptyMessage,
}) => {
  const inbox = useApprovalInbox({
    stage,
    canWrite,
    canWriteEntity,
    listMode,
    queueType,
    queueParams,
    onNotify,
  });
  return (
    <ApprovalInboxUI
      inbox={inbox}
      canWrite={canWrite}
      canWriteEntity={canWriteEntity}
      emptyMessage={emptyMessage}
    />
  );
};

/**
 * Generic approval inbox. Plugins pass body/row content only.
 * Pass `inbox` from useApprovalInbox() when parent needs refetch.
 */
const ApprovalInbox = ({
  canWrite = false,
  canWriteEntity,
  stage = "admin",
  listMode = "pending",
  queueType = "all",
  queueParams = { pageSize: 100 },
  onNotify,
  inbox,
  emptyMessage,
  filterSections = [],
  activeFilterCount = 0,
  onApplyFilters,
  onResetFilters,
}) => {
  const msg =
    emptyMessage ??
    (listMode === "history"
      ? "Geçmiş onay kaydı bulunmuyor."
      : listMode === "all"
        ? "Onay kaydı bulunmuyor."
        : "Bekleyen onay bulunmuyor.");

  if (inbox) {
    return (
      <ApprovalInboxUI
        inbox={inbox}
        canWrite={canWrite}
        canWriteEntity={canWriteEntity}
        stage={stage}
        emptyMessage={msg}
        filterSections={filterSections}
        activeFilterCount={activeFilterCount}
        onApplyFilters={onApplyFilters}
        onResetFilters={onResetFilters}
      />
    );
  }
  return (
    <ApprovalInboxContainer
      canWrite={canWrite}
      canWriteEntity={canWriteEntity}
      stage={stage}
      listMode={listMode}
      queueType={queueType}
      queueParams={queueParams}
      onNotify={onNotify}
      emptyMessage={msg}
    />
  );
};

export default ApprovalInbox;
